import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

const GENEROS = [
  { value: 'masculino',       label: 'Masculino' },
  { value: 'femenino',        label: 'Femenino' },
  { value: 'otro',            label: 'Otro' },
  { value: 'no_especificado', label: 'No especificado' },
];

const TIPOS_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'desconocido'];

const emptyForm = {
  nombre: '', apellidoPaterno: '', apellidoMaterno: '', fechaNacimiento: '',
  genero: 'no_especificado', curp: '', telefono: '', email: '',
  tipoSangre: '', alergias: '', medicoTitularId: '',
};

const fieldCls = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback adicional cuando el paciente fue creado */
  onCreated?: (paciente: any) => void;
}

export function NuevoPacienteDialog({ open, onOpenChange, onCreated }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);

  const f = (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/pacientes', data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pacientes'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Paciente registrado');
      onCreated?.(data);
      onOpenChange(false);
      setForm(emptyForm);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el paciente')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = { ...form };
    // Campos opcionales: si quedan en '' no se mandan, para que el backend
    // los trate como ausentes en vez de fallar su validación (@IsEmail, @IsDate, etc.
    // no aceptan string vacío aunque el campo sea @IsOptional).
    for (const key of Object.keys(payload)) {
      if (payload[key] === '') delete payload[key];
    }
    createMutation.mutate(payload);
  }

  return (
    <Dialog.Root open={open} onOpenChange={v => { onOpenChange(v); if (!v) setForm(emptyForm); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 focus:outline-none max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-text-primary">Nuevo paciente</Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Nombre <span className="text-red-500">*</span></label>
                <input required value={form.nombre} onChange={f('nombre')} placeholder="Juan" className={fieldCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Apellido paterno</label>
                <input value={form.apellidoPaterno} onChange={f('apellidoPaterno')} placeholder="García" className={fieldCls} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Apellido materno</label>
                <input value={form.apellidoMaterno} onChange={f('apellidoMaterno')} placeholder="López" className={fieldCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Fecha de nacimiento</label>
                <input type="date" value={form.fechaNacimiento} onChange={f('fechaNacimiento')} className={fieldCls} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Género</label>
                <select value={form.genero} onChange={f('genero')} className={fieldCls}>
                  {GENEROS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Tipo de sangre</label>
                <select value={form.tipoSangre} onChange={f('tipoSangre')} className={fieldCls}>
                  <option value="">No especificado</option>
                  {TIPOS_SANGRE.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Teléfono <span className="text-red-500">*</span></label>
                <input required value={form.telefono} onChange={f('telefono')} placeholder="55 1234 5678" className={fieldCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
                <input type="email" value={form.email} onChange={f('email')} placeholder="correo@ejemplo.com" className={fieldCls} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">CURP</label>
              <input value={form.curp} onChange={f('curp')} placeholder="XAXX010101HXXXXX00" maxLength={18} className={fieldCls} />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Médico titular <span className="text-red-500">*</span></label>
              <select required value={form.medicoTitularId} onChange={f('medicoTitularId')} className={fieldCls}>
                <option value="">Seleccionar médico</option>
                {(medicos as any[]).map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Alergias conocidas</label>
              <textarea value={form.alergias} onChange={f('alergias')} rows={2}
                placeholder="Penicilina, látex, etc." className={`${fieldCls} resize-none`} />
            </div>

            <div className="flex gap-3 pt-2">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                Cancelar
              </Dialog.Close>
              <button type="submit" disabled={createMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                {createMutation.isPending ? 'Guardando...' : 'Registrar paciente'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
